import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  ListGroup,
  ListGroupItem,
  Container,
  DropdownItem,
  Spinner,
  Button
} from "reactstrap";
import { Badge } from "@mui/material";
import SimpleHeader from "components/Headers/SimpleHeader";
import { useAuth } from "context/AuthContext";
import userServices from "services/userServices";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";
import DropdownFilter from "utils/DropdownFilter";
import Pagination from "views/pages/components/Pagination";

const UserNotifications = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hoveredNotificationId, setHoveredNotificationId] = useState(null);
  const [notificationTypeFilter, setNotificationTypeFilter] = useState(
    helpers.NOTIFICATION_FILTERS.ALL
  );
  const handleClearFilters = () => {
    setNotificationTypeFilter(helpers.NOTIFICATION_FILTERS.ALL);
    setCurrentPage(0);
  };
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const fetchNotifications = React.useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const response = await userServices.fetchNotifications({
        page: currentPage + 1,
        limit: rowsPerPage,
        type: notificationTypeFilter !== helpers.NOTIFICATION_FILTERS.ALL ? notificationTypeFilter : undefined
      });
      const dataObj = response?.data || response;
      const notifsList = dataObj?.notifications || (Array.isArray(dataObj) ? dataObj : []);
      const pagination = response?.pagination || response?.meta || dataObj || {};

      setNotifications(notifsList);
      setTotalRows(pagination.total || pagination.totalItems || 0);
      setUnreadCount(pagination.unreadCount || 0);
    } catch (error) {
      console.error(error);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [currentPage, rowsPerPage, notificationTypeFilter]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    fetchNotifications(true);
    const intervalNotif = setInterval(() => fetchNotifications(false), 15000);

    const handleNotificationsRead = () => {
      fetchNotifications(false);
    };

    window.addEventListener("notificationsRead", handleNotificationsRead);
    return () => {
      clearInterval(intervalNotif);
      window.removeEventListener("notificationsRead", handleNotificationsRead);
    };
  }, [isAuthenticated, navigate, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      const res = await userServices.updateNotification(notificationId, { read: true });
      const isSuccess = res?.success || (!res?.error && res);
      if (isSuccess) {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) =>
            notification._id === notificationId
              ? { ...notification, read: true }
              : notification
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
        window.dispatchEvent(new Event("notificationsRead"));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      const res = await userServices.markAllNotificationsAsRead();
      const isSuccess = res?.success || (!res?.error && res);
      if (isSuccess) {
        await fetchNotifications(false);
        window.dispatchEvent(new Event("notificationsRead"));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setMarkingAllRead(false);
    }
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="center-loader">
        <div className="center-loader-content">
          <Spinner/>
          <p className="mt-3 text-muted">
            {t("common.loading", "Loading....")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SimpleHeader
        name={t("page.notifications.adminNotifications", "Admin Notifications")}
        parentName={t("sideNav.notifications", "Notification")}
      />

      <Container className="mt--6" fluid>
        <Card style={{ position: "relative" }}>
          <CardHeader className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">

            <h5 className="h3 mb-2 mb-md-0">
              {t("page.notifications.adminNotifications", "Admin Notifications")}
            </h5>

            <div className="d-flex align-items-center gap-3 flex-column flex-sm-row">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={markAllAsRead}
                disabled={markingAllRead || unreadCount === 0}
              >
                {markingAllRead
                  ? t("page.notifications.markingAllRead", "Marking...")
                  : t("page.notifications.markAllAsRead", "Mark all as read")}
              </button>

              <div className="listing-header-right d-flex align-items-center">
                <span className="text-sm font-weight-600 text-muted ml-2 mr-2">
                  {t("table.filterBy", "Filter By")}
                </span>
                <DropdownFilter
                  value={notificationTypeFilter}
                  options={helpers.getFilter("NOTIFICATION_TYPE", t)}
                  onChange={(value) => {
                    setNotificationTypeFilter(value);
                    setCurrentPage(0);
                  }}
                  buttonClass="btn-neutral filter-select2"
                />
                <Button
                  size="sm"
                  className="ml-2 btn-neutral text-danger font-weight-600"
                  onClick={handleClearFilters}
                >
                  {t("common.clearFilters", "Clear Filters")}
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody className="p-0">
            {notifications.length > 0 ? (
              <ListGroup flush>
                {notifications.map((notification) => (
                  <ListGroupItem
                    key={notification._id}
                    tag="div"
                    onMouseEnter={() =>
                      setHoveredNotificationId(notification._id)
                    }
                    onMouseLeave={() => setHoveredNotificationId(null)}
                    className={`list-group-item-action py-4 px-4 position-relative sidenav-toggler ${
                      !notification.read ? "bg-blue-50" : "bg-white"
                    } hover:bg-blue-100 transition`}
                    onClick={() =>
                      helpers.handleOpenNotification({
                        notification,
                        navigate,
                        markAsRead,
                      })
                    }
                  >
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                      <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1">
                        <div className="flex-shrink-0 mr-4">
                          {helpers.getNotificationIcon(notification.type)}
                        </div>

                        <div className="flex flex-col truncate min-w-0">
                          <h5
                            className={`mb-0 truncate subtitle-large ${
                              !notification.read
                                ? "font-bold text-[#111]"
                                : "text-gray-800"
                            }`}
                          >
                            {notification.subject ||
                              t("page.notifications.untitled", "Notification")}
                          </h5>

                          {notification.subtitle && (
                            <small className="subtitle-small truncate">
                              {notification.subtitle}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0 flex-shrink-0 self-end self-sm-center">
                        {!notification.read &&
                          hoveredNotificationId === notification._id && (
                            <button
                              className="btn btn-sm btn-outline-primary mr-4"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification._id);
                              }}
                            >
                              {t("page.notifications.markAsRead", "Mark as read")}
                            </button>
                          )}
                        {!notification.read && (
                          <Badge
                            badgeContent="!"
                            className="custom-badge mr-3"
                          />
                        )}

                        <small className="whitespace-nowrap">
                          {helpers.getRelativeTime(notification.createdAt, t)}
                        </small>
                      </div>
                    </div>

                    {notification.description && (
                      <p className="text-sm mt-2 mb-0 text-gray-700 truncate">
                        {notification.description}
                      </p>
                    )}
                  </ListGroupItem>
                ))}
              </ListGroup>
            ) : (
              <div className="d-flex flex-column justify-content-center align-items-center py-6">
                <span className="text-sm md:text-base">
                  {t("page.notifications.noNotifications", "No Notifications Yet")}
                </span>
              </div>
            )}

            <DropdownItem divider />

            <Pagination
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              totalRows={totalRows}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(size) => {
                setRowsPerPage(size);
                setCurrentPage(0);
              }}
              t={t}
            />
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default UserNotifications;
